<template>
  <div>
    <form-section>
      <div class="date-time-ct">
        <form-item
          :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_Starts')"
          class="input"
          prop="start_date"
        >
          <div class="with-description">
            <div class="description">
              {{ $t('CampaignForm_Schedule_ScheduleSelectorTriggered_From') }}
            </div>
            <el-date-picker
              format="MMMM d, yyyy"
              value-format="yyyy-MM-dd"
              v-model="model.start_date"
              class="date-input"
              :picker-options="pickerOptions"
              @change="dateChanged('start_date', $event)"
              data-qa="shedule-start-date-input"
            />
          </div>
        </form-item>
        <form-item
          :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_Time')"
          class="time-input input"
          prop="start_date_time"
        >
          <el-time-picker
            format="HH:mm"
            value-format="HH:mm"
            v-model="model.start_date_time"
            @change="timeChanged('start_date_time', $event)"
            data-qa="shedule-start-time-input"
          />
        </form-item>
        <form-item
          :label="$t('CampaignForm_Schedule_SendLater_Timezone')"
          class="timezone-select"
          prop="timezone"
        >
          <el-select
            v-model="model.timezone"
            filterable
            clearable
            data-qa="shedule-start-timezone-input"
          >
            <el-option
              v-for="(label, value) in timezones"
              :key="value"
              :value="value"
              :label="label"
            >
              <span v-tooltip>{{ label }}</span>
            </el-option>
          </el-select>
        </form-item>
      </div>
      <form-item
        prop="ends"
        :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_Ends')"
        class="m-top-32"
      >
        <radio-group :change="onChange" v-model="ends" inline>
          <radio
            border
            :label="false"
            :title="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_Never')"
            data-qa="schedule-never-option-radio"
          />
          <radio-custom
            :border="true"
            :label="true"
            data-qa="schedule-on-option-radio"
          >
            <div class="radio-input">
              <div class="input-description">
                {{ $t('CampaignForm_Schedule_ScheduleSelectorTriggered_On') }}
              </div>
              <div class="radio-input date-time-ct">
                <form-item class="date-input input" prop="end_date">
                  <el-date-picker
                    format="MMMM d, yyyy"
                    value-format="yyyy-MM-dd"
                    v-model="model.end_date"
                    :picker-options="pickerOptions"
                    :disabled="!ends"
                    @change="dateChanged('end_date', $event)"
                    data-qa="shedule-on-date-input"
                  />
                </form-item>
                <form-item class="time-input" prop="end_date_time">
                  <el-time-picker
                    format="HH:mm"
                    value-format="HH:mm"
                    v-model="model.end_date_time"
                    :disabled="!ends"
                    @change="timeChanged('end_date_time', $event)"
                    data-qa="shedule-on-time-input"
                  />
                </form-item>
              </div>
            </div>
          </radio-custom>
        </radio-group>
      </form-item>

      <div v-if="model.trigger !== campaignTriggerTypes.TRIGGER_ACCUMULATION">
        <div class="divider"></div>
        <form-item
          :label="
            $t('CampaignForm_Schedule_ScheduleSelectorTriggered_TriggerOn')
          "
          prop="send_days"
        >
          <checkbox-group
            v-model="model.send_days"
            :labels="dayOfWeekLabels"
            :border="true"
            :keys-is-int="true"
          />
        </form-item>
        <div class="m-top-32 date-time-ct">
          <form-item
            :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_From')"
            class="time-input input"
            prop="start_time"
          >
            <el-time-picker
              format="HH:mm"
              value-format="HH:mm"
              v-model="model.start_time"
              data-qa="trigger-start-time-input"
            />
          </form-item>
          <form-item
            :label="$t('CampaignForm_Schedule_ScheduleSelectorTriggered_To')"
            class="time-input"
            prop="end_time"
          >
            <el-time-picker
              format="HH:mm"
              value-format="HH:mm"
              v-model="model.end_time"
              data-qa="trigger-end-time-input"
            />
          </form-item>
        </div>
      </div>
    </form-section>
    <offline-users-component v-if="showOfflineUsers" :model="model" />
    <persist-content-component
      v-if="model.inbox && !isWorkflowCampaign"
      :model="model"
    />
  </div>
</template>

<script>
import FormSection from '@/common/components/form/FormSection'
import FormItem from '@/common/components/form/FormItem'
import { dayOfWeekLabels } from '@/modules/campaign-manage/enum/Labels'
import CheckboxGroup from '@/common/components/form/CheckboxGroup'
import RadioGroup from '@/common/components/form/RadioGroup'
import Radio from '@/common/components/form/Radio'
import RadioCustom from '@/modules/campaign-manage/components/locations/RadioCustom'
import OfflineUsersComponent from '@/modules/campaign-manage/components/schedule/OfflineUsersComponent'
import PersistContentComponent from '@/modules/campaign-manage/components/schedule/PersistContentComponent'
import moment from 'moment'
import 'moment-timezone'
import { mapGetters } from 'vuex'
import { campaignTypeEnum } from '@/modules/campaigns/enum/CampaignTypeEnum'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { campaignEventModeEnum } from '@/modules/campaign-manage/enum/CampaignEventModeEnum'

export default {
  name: 'ScheduleSelectorTriggered',
  components: {
    PersistContentComponent,
    OfflineUsersComponent,
    RadioCustom,
    Radio,
    RadioGroup,
    CheckboxGroup,
    FormItem,
    FormSection
  },
  mounted() {
    this.ends = !!this.model.end_date
  },
  props: {
    model: {
      type: Object
    }
  },
  data() {
    return {
      dayOfWeekLabels,
      campaignTriggerTypes,
      ends: false,
      now: moment().startOf('date'),
      pickerOptions: {
        shortcuts: [
          {
            text: 'Now',
            onClick: vm => vm.$emit('pick', new Date())
          }
        ],
        disabledDate: date => moment(date).isBefore(this.now, 'date')
      }
    }
  },
  methods: {
    onChange(val) {
      if (!val) {
        this.model.end_date = this.model.end_date_time = null
      }
    },
    dateChanged(field, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.model[field] = moment()
            .tz(this.model.timezone)
            .format('Y-MM-DD')
        })
      }
    },
    timeChanged(field, newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.model[field] = moment()
            .tz(this.model.timezone)
            .format('HH:mm')
        })
      }
    }
  },
  computed: {
    ...mapGetters(['timezones']),
    isWorkflowCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_WORKFLOW
    },
    isStandardCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_NOTIFICATION
    },
    isInappCampaign() {
      return this.model.type === campaignTypeEnum.TYPE_INAPP
    },
    showOfflineUsers() {
      if (this.isStandardCampaign) {
        return (
          this.model.ios_push ||
          this.model.android_push ||
          this.model.web_push ||
          this.model.inapp_ios ||
          this.model.inapp_android ||
          this.model.onsite
        )
      } else if (this.isInappCampaign) {
        return this.model.event_mode === campaignEventModeEnum.SEND
      } else {
        return false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.date-time-ct {
  display: flex;

  .date-input {
    width: 200px;
  }
  .time-input {
    width: 100px;
  }
  .timezone-select {
    width: 375px;
  }
  .input {
    margin-right: 16px;
  }
}
.divider {
  margin: 24px -20px;
  border-top: 1px solid #ebeef5;
}

/deep/ .el-radio {
  height: 60px !important;
}
.with-description {
  display: flex;
  align-items: center;
  .description {
    padding-right: 16px;
    color: #7e8596;
    font-size: 14px;
  }
}
.radio-input {
  display: flex;
  align-items: center;
}
.input-description {
  margin-right: 50px;
}
/deep/ .el-form-item__label {
  color: #5c6374;
  font-size: 14px;
  line-height: 22px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/schedule/ScheduleSelectorTriggered.vue